import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { AlertController, LoadingController, ModalController, NavParams, ToastController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { SnapBricksService } from 'src/app/services/snapBricks/snap-bricks.service';
import { ConnectivityService } from 'src/app/services/connectivity/connectivity.service';
import Swal, { SweetAlertIcon } from 'sweetalert2';
import * as firebase from 'firebase/app';
import { NO_SECTION_KEY, SectionGroup, groupBySection } from 'src/app/shared/event-products/product-sections';
import { isKitchenLine } from 'src/app/shared/kitchen/kitchen.model';
import {
	ORIGINAL_VARIANT_KEY, hasVariants, setVariantQuantity, splitByVariant, variantBreakdown, variantLabel, variantOptions, variantQuantity,
	variantTotal,
} from 'src/app/shared/event-products/product-variants';
import { COURTESY_PAYMENT, companyKey, knownCompanies } from 'src/app/shared/courtesy/courtesy.model';

/** Una pestaña de sección de la caja, con cuántos productos tiene. */
interface SectionTab {
	key: string;
	name: string;
	count: number;
}

@Component({
	selector: 'app-create-order-event',
	templateUrl: './create-order-event.component.html',
	styleUrls: ['./create-order-event.component.scss'],
})
export class CreateOrderEventComponent implements OnInit {

	categoryKey: any
	event_key: any
	private dataSubscription: Subscription; // Variable para almacenar la suscripción
	copyOfOriginalBricks: any = [];
	/** Lo que se pinta: copyOfOriginalBricks filtrado por sección y buscador. */
	visibleBricks: any = [];
	searchTerm: string = '';
	/**
	 * Secciones de la zona ("Botellas", "Cócteles"…) como pestañas: un cajero
	 * con pantalla táctil y sin teclado llega al producto con un toque en vez
	 * de escribir. Vacío si ningún producto de la zona tiene sección.
	 */
	sections: SectionTab[] = [];
	/** '' = todas; NO_SECTION_KEY = los que no tienen sección. */
	selectedSection: string = '';
	/** Con "Todas" y secciones, la cuadrícula se pinta por grupos con su encabezado. */
	visibleGroups: SectionGroup<any>[] = [];
	readonly NO_SECTION_KEY = NO_SECTION_KEY;
	/**
	 * Dirección del orden por precio del catálogo. Se recuerda entre órdenes
	 * porque la caja abre este modal por cada venta y quien cobra suele
	 * preferir siempre la misma vista.
	 */
	priceOrder: 'asc' | 'desc' = 'asc';
	private static readonly PRICE_ORDER_KEY = 'caja_price_order';
	cartUser: any = [];
	subtotalCollapsed: boolean = true;
	segmentS: any = "";
	totalOrder: number = 0;

	categoryEventKey: any = ""
	eventKey: any = ""
	// La plantilla lo interpola desde siempre sin que existiera; el pipe
	// currency ya pone el símbolo, así que se deja vacío para no duplicarlo.
	currency: string = ""

	/**
	 * Zonas entre las que elegir de dónde sale la orden. Llega vacío cuando
	 * la caja ya trabaja con una zona fija; con zonas, no se vende hasta
	 * escoger una y solo se muestran sus productos.
	 */
	zones: any[] = []
	zoneSelected: any = null
	/** Llaves de la zona elegida y de sus sub-zonas: así se filtra el catálogo. */
	private zoneKeys: string[] = []
	/**
	 * Zona fija de la caja (rol 8), con sus sub-zonas, para mostrar solo lo
	 * que se vende en esa barra. Sin ella, una caja con zona fija veía los
	 * productos de todas las barras del evento (y cada copia por barra
	 * aparecía repetida).
	 */
	fixedZone: any = null
	/** Productos de la zona antes del buscador, para el contador. */
	zoneBrickCount: number = 0

	// ── Regalías ────────────────────────────────────────────────────────────
	// Una orden cuyo carrito suma $0 no se cobra: se regala, casi siempre a
	// una empresa patrocinadora o invitada. En vez de pedir efectivo/POS se
	// pide a quién va, para que el cliente del evento pueda ver después
	// cuánto se regaló y a qué empresa.
	/** Empresa a la que se regala la orden en curso. */
	courtesyCompany: string = ''
	/**
	 * Empresas ya usadas (las órdenes que ve el monitor más las de este
	 * dispositivo), para escogerlas con un toque y no escribir la misma de
	 * tres formas distintas.
	 */
	courtesyCompanies: string[] = []
	private static readonly COURTESY_KEY = 'caja_courtesy_companies';
	private static readonly COURTESY_MAX = 15;

	constructor(
		public navParams: NavParams,
		private snapBricksService: SnapBricksService,
		public loadingCtrl: LoadingController,
		public toastController: ToastController,
		private changeDetectorRef: ChangeDetectorRef,
		public auth: AuthService,
		public api: ApiService,
		public alertController: AlertController,
		public modalController: ModalController,
		private connectivity: ConnectivityService,
	) {
	}

	/**
	 * Sin conexión o con señal débil se lee de caché sin esperar al servidor.
	 * Bricks y presentaciones llegan en vivo por SnapBricksService, así que
	 * la copia local es la misma que tendría el servidor.
	 */
	private readOptions(): firebase.firestore.GetOptions | undefined {
		return this.connectivity.preferCache ? { source: 'cache' } : undefined;
	}



	ngOnInit() {
		this.priceOrder = this.readPriceOrder();
		this.categoryEventKey  = this.navParams.get('categoryEventKey');
    	this.eventKey  = this.navParams.get('eventKey');
		this.zones = this.navParams.get('zones') || [];
		this.fixedZone = this.navParams.get('fixedZone') || null;
		this.courtesyCompanies = knownCompanies([], (this.navParams.get('courtesyCompanies') || []).concat(this.readCourtesyCompanies()));
		// Caja con zona fija: solo su barra (y sub-zonas). Si no llegó el
		// objeto de la zona, al menos la llave.
		if (this.categoryEventKey) {
			this.zoneKeys = this.fixedZone ? this.collectZoneKeys(this.fixedZone) : [this.categoryEventKey];
			if (this.zoneKeys.indexOf(this.categoryEventKey) < 0) this.zoneKeys.push(this.categoryEventKey);
		}
		// Con una sola zona posible no hay nada que preguntar.
		if (!this.categoryEventKey && this.zones.length === 1) {
			this.applyZone(this.zones[0]);
		}
		this.dataSubscription = this.snapBricksService.data$.subscribe(bricks => {
			console.log('📦 [BRICKS DATA]:', bricks);
			if (bricks && bricks.length > 0 && bricks[0]) {
				console.log('📦 [PRIMER BRICK OBJETO]:', bricks[0]);
				console.log('📦 [brick.name]:', bricks[0].name);
				console.log('📦 [brick.presentation]:', bricks[0].presentation);
			}
			this.copyOfOriginalBricks = JSON.parse(JSON.stringify(bricks));
			this.checkQuantityBricks()
			// El inventario llega en vivo y reemplaza la lista: el filtro se
			// vuelve a aplicar para que la búsqueda no se pierda al refrescar.
			this.applySearch()
			this.changeDetectorRef.detectChanges()
		});

	}

	// ═══════════════════════════════════════════════════════════════════════
	//  BUSCADOR
	// ═══════════════════════════════════════════════════════════════════════

	/** Sin acentos ni mayúsculas: "cafe" encuentra "Café". */
	private normalize(text: any): string {
		return String(text || '')
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.toLowerCase()
			.trim();
	}

	/**
	 * Filtra sin clonar: el carrito escribe cantidades sobre estos mismos
	 * objetos, así que la lista visible debe apuntar a los originales.
	 * Varias palabras se exigen todas: "cerveza 355" solo deja lo que
	 * contenga ambas, en cualquier orden.
	 */
	applySearch() {
		const base = this.bricksOfZone();
		this.zoneBrickCount = base.length;
		this.refreshSections(base);

		const inSection = this.selectedSection
			? base.filter(brick => this.sectionKeyOf(brick) === this.selectedSection)
			: base;

		const term = this.normalize(this.searchTerm);
		let list = inSection;
		if (term) {
			const words = term.split(/\s+/).filter(w => w.length > 0);
			list = inSection.filter(brick => {
				const haystack = this.normalize([
					brick && brick.name,
					brick && brick.presentation && brick.presentation.presentation,
					brick && brick.productCode,
					brick && brick.category_name,
					brick && brick.section,
					brick && this.plainText(brick.description),
				].join(' '));
				return words.every(w => haystack.indexOf(w) >= 0);
			});
		}
		this.visibleBricks = this.sortByPrice(list);
		// Viendo todas las secciones, cada una va con su encabezado; dentro
		// del grupo se conserva el orden por precio.
		this.visibleGroups = (!this.selectedSection && this.sections.length > 0)
			? groupBySection(this.visibleBricks, brick => brick && brick.section)
			: [];
	}

	// ═══════════════════════════════════════════════════════════════════════
	//  SECCIONES
	// ═══════════════════════════════════════════════════════════════════════

	private sectionKeyOf(brick: any): string {
		const groups = groupBySection([brick], b => b && b.section);
		return groups.length > 0 ? groups[0].key : NO_SECTION_KEY;
	}

	/**
	 * Pestañas a partir de los productos de la zona. Si ninguno tiene
	 * sección no hay pestañas y la caja se ve como siempre. Si la sección
	 * elegida desaparece (cambió la zona o el producto), se vuelve a "Todas".
	 */
	private refreshSections(base: any[]) {
		const groups = groupBySection(base, brick => brick && brick.section);
		const hasSections = groups.some(g => g.key !== NO_SECTION_KEY);
		this.sections = hasSections ? groups.map(g => ({ key: g.key, name: g.name, count: g.items.length })) : [];
		if (this.selectedSection && !this.sections.some(s => s.key === this.selectedSection)) {
			this.selectedSection = '';
		}
	}

	selectSection(key: string) {
		if (this.selectedSection === key) return;
		this.selectedSection = key;
		this.applySearch();
	}

	get selected_section_name(): string {
		const tab = this.sections.filter(s => s.key === this.selectedSection)[0];
		return tab ? tab.name : '';
	}

	// ═══════════════════════════════════════════════════════════════════════
	//  ORDEN POR PRECIO
	// ═══════════════════════════════════════════════════════════════════════

	/**
	 * Los bricks llegan en el orden en que responde cada listener de
	 * presentaciones, o sea ninguno. Se ordenan por el precio de la
	 * presentación que vende la caja y, a igual precio, por nombre, para que
	 * la cuadrícula no baile cuando el inventario se refresca en vivo.
	 * Ordena en sitio: `bricksOfZone()` y el filtro ya devuelven arreglos
	 * nuevos, y los objetos siguen siendo los originales (el carrito escribe
	 * sobre ellos).
	 */
	private sortByPrice(bricks: any[]): any[] {
		const sign = this.priceOrder === 'desc' ? -1 : 1;
		return bricks.sort((a, b) => {
			const diff = this.priceOf(a) - this.priceOf(b);
			if (diff !== 0) return diff * sign;
			return String(a && a.name || '').localeCompare(String(b && b.name || ''), 'es', { sensitivity: 'base' });
		});
	}

	/** El precio puede venir como texto desde el importador; sin precio cuenta como 0. */
	private priceOf(brick: any): number {
		const price = Number(brick && brick.presentation && brick.presentation.price);
		return isFinite(price) ? price : 0;
	}

	get price_order_label(): string {
		return this.priceOrder === 'desc' ? 'mayor a menor' : 'menor a mayor';
	}

	togglePriceOrder() {
		this.priceOrder = this.priceOrder === 'asc' ? 'desc' : 'asc';
		this.savePriceOrder();
		this.applySearch();
	}

	/** localStorage puede fallar (modo privado, cuota); la caja sigue con el orden por defecto. */
	private readPriceOrder(): 'asc' | 'desc' {
		try {
			return localStorage.getItem(CreateOrderEventComponent.PRICE_ORDER_KEY) === 'desc' ? 'desc' : 'asc';
		} catch (e) {
			return 'asc';
		}
	}

	private savePriceOrder() {
		try {
			localStorage.setItem(CreateOrderEventComponent.PRICE_ORDER_KEY, this.priceOrder);
		} catch (e) { /* sin persistencia, nada más */ }
	}

	/**
	 * La descripción se escribe en un editor de texto enriquecido y llega
	 * con HTML; para buscar solo interesa el texto.
	 */
	private plainText(html: any): string {
		return String(html || '')
			.replace(/<[^>]*>/g, ' ')
			.replace(/&nbsp;|&#160;/gi, ' ')
			.replace(/&amp;/gi, '&')
			.replace(/\s+/g, ' ');
	}

	clearSearch() {
		this.searchTerm = '';
		this.applySearch();
	}

	get is_searching(): boolean {
		return this.normalize(this.searchTerm).length > 0;
	}

	// ═══════════════════════════════════════════════════════════════════════
	//  ZONA DE SALIDA
	// ═══════════════════════════════════════════════════════════════════════

	/** Hay zonas para elegir y todavía no se escogió ninguna. */
	get must_pick_zone(): boolean {
		return this.zones.length > 0 && !this.categoryEventKey;
	}

	/**
	 * Solo lo que cuelga de la zona de salida (elegida o fija) y que el
	 * monitor no haya ocultado. Sin zona elegida, nada. Sin ninguna zona
	 * conocida (caja sin configurar), el catálogo completo como antes.
	 */
	private bricksOfZone(): any[] {
		const offered = this.copyOfOriginalBricks.filter(brick => brick && !brick.hidden);
		if (this.zoneKeys.length > 0) return offered.filter(brick => this.zoneKeys.indexOf(brick.category) >= 0);
		if (this.zones.length > 0) return [];
		return offered;
	}

	/** Sin existencias: se muestra como agotado y no se puede añadir. */
	isSoldOut(brick: any): boolean {
		const inventory = brick && brick.presentation ? Number(brick.presentation.inventory) : 0;
		return !(inventory > 0);
	}

	/** La zona y todas sus sub-zonas, que es donde viven los productos. */
	private collectZoneKeys(zone: any, keys: string[] = []): string[] {
		if (!zone) return keys;
		if (zone.$key) keys.push(zone.$key);
		if (Array.isArray(zone.sub_zone)) {
			zone.sub_zone.forEach(sub => this.collectZoneKeys(sub, keys));
		}
		return keys;
	}

	private applyZone(zone: any) {
		this.zoneSelected = zone;
		this.categoryEventKey = zone.$key;
		this.zoneKeys = this.collectZoneKeys(zone);
		// Cada barra tiene sus propias secciones.
		this.selectedSection = '';
		this.applySearch();
	}

	async selectZone(zone: any) {
		if (!zone || zone.$key === this.categoryEventKey) return;

		// Una orden sale de una sola zona: cambiarla vacía lo ya elegido.
		if (this.cartUser.length > 0) {
			const result: any = await Swal.fire({
				title: '¿Cambiar de zona?',
				text: 'Los productos que ya agregaste se quitan de la orden.',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonText: 'Sí, cambiar',
				cancelButtonText: 'Seguir aquí',
				confirmButtonColor: '#912721',
				cancelButtonColor: '#92949c',
			});
			if (!result || !result.value) return;
			this.clearCart();
		}

		this.applyZone(zone);
	}

	private clearCart() {
		this.copyOfOriginalBricks.forEach(brick => {
			if (brick && brick.presentation && brick.presentation.quantity > 0) {
				brick.presentation.clicked = false;
				brick.presentation.quantity = 0;
				brick.presentation.subtotal = 0;
				delete brick.presentation.variant_quantities;
			}
		});
		this.cartUser = [];
		this.totalOrder = 0;
	}

	checkQuantityBricks() {

		if (this.cartUser.length > 0) {
			this.totalOrder = 0
			this.cartUser.forEach(elementCart => {
				const existElement = this.copyOfOriginalBricks.find(allBricks => allBricks.$key === elementCart.$key);
				// Si se encuentra una coincidencia, modificar el objeto del primer arreglo
				if (existElement) {
					existElement.presentation.clicked = elementCart.presentation.clicked; // Modificar el campo que necesites
					existElement.presentation.quantity = elementCart.presentation.quantity; // Modificar el campo que necesites
					existElement.presentation.subtotal = elementCart.presentation.subtotal; // Modificar el campo que necesites
					// El desglose por variante vive en la misma presentación.
					if (elementCart.presentation.variant_quantities) {
						existElement.presentation.variant_quantities = elementCart.presentation.variant_quantities;
					}
					this.totalOrder += existElement.presentation.subtotal
				}

			});

			//meter la cantidad de prod 
			console.log("verificar inventarios");
		}
	}

	ngOnDestroy(): void {
		//Called once, before the instance is destroyed.
		//Add 'implements OnDestroy' to the class.
		if (this.dataSubscription) {
			this.dataSubscription.unsubscribe();
		}
	}

	closeModal() {
		this.modalController.dismiss();
	}

	/**
	 * `variant`: opción del producto sobre la que se suma o resta ('' = el
	 * original). Solo la mandan las filas de un producto con variantes.
	 */
	public cartTransactionFn = async (brick: any, transactionParam: string, variant: string = '') => {

		const loader = await this.loadingCtrl.create({
			backdropDismiss: false,
		})
		await loader.present()

		const actFunctions = {
			'add': async () => {
				await this.toggleAddValue(brick, variant)
			},
			'plusOne': async () => {
				await this.counterButton(brick, 'add', variant)
			},
			'removeOne': async () => {
				await this.counterButton(brick, 'remove', variant)
			}
		}
		await actFunctions[transactionParam]();
		const loading = await this.loadingCtrl.getTop()
		if (loading) {
			await loading.dismiss()
		}

	}

	addBrick(brick){
		if(!brick.presentation.clicked && brick.presentation.quantity == 0){
			this.cartTransactionFn(brick,"add")
		}else{
			this.cartTransactionFn(brick,"plusOne")
		}
	}

	async toggleAddValue(brick, variant: string = '') {
		brick.presentation.clicked = true;
		brick.presentation.quantity = 0;
		delete brick.presentation.variant_quantities;
		await this.counterButton(brick, 'add', variant);
	}

	/**
	 * Con variantes, `presentation.quantity` sigue siendo el total del
	 * producto (es lo que comparan el inventario y el subtotal) y el desglose
	 * por opción va en `presentation.variant_quantities`; sin variantes se
	 * suma y resta directo como siempre.
	 */
	async counterButton(brick, action, variant: string = '') {
		const withVariants = this.hasVariants(brick);
		// El producto puede ganar o perder variantes en vivo mientras está en
		// el carrito: el desglose se alinea con lo que tiene ahora.
		if (withVariants) this.seedVariantQuantities(brick);
		else delete brick.presentation.variant_quantities;
		// La presentación llega de Firestore sin `quantity` (y el inventario a
		// veces como texto): se compara con números o `40 > undefined` da
		// false y el producto parece agotado en el primer toque.
		const quantity = Number(brick.presentation.quantity) || 0;
		const inventory = Number(brick.presentation.inventory) || 0;
		if (action === 'add') {
			if (inventory > quantity) {
				console.log("agregar");
				if (withVariants) this.setVariantQuantity(brick, variant, this.variantQty(brick, variant) + 1);
				else brick.presentation.quantity = quantity + 1;
				brick.presentation.clicked = true;
				await this.updateCartUser(brick)
			} else {
				const toast = await this.toastController.create({
					message: "Por el momento NO existe disponibilidad de este producto",
					duration: 2000,
					color: 'danger'
				})
				await toast.present();
			}
		} else if (action === "remove") {

			if (withVariants) this.setVariantQuantity(brick, variant, this.variantQty(brick, variant) - 1);
			else brick.presentation.quantity = quantity - 1;
			if (brick.presentation.quantity <= 0) {
				brick.presentation.clicked = false;
				delete brick.presentation.variant_quantities;
				await this.deleteBrick(brick)
			} else {
				await this.updateCartUser(brick)
			}

		}
	}

	// ═══════════════════════════════════════════════════════════════════════
	//  VARIANTES ("Squeeze": Original o Whiskey)
	// ═══════════════════════════════════════════════════════════════════════

	hasVariants(brick: any): boolean {
		return hasVariants(brick);
	}

	/** Opciones que se pintan en la tarjeta: el original y cada variante. */
	optionsOf(brick: any): string[] {
		return variantOptions(brick);
	}

	optionLabel(variant: string): string {
		return variantLabel(variant);
	}

	/** Unidades de una opción en la orden en curso. */
	variantQty(brick: any, variant: string): number {
		return variantQuantity(brick && brick.presentation, variant);
	}

	private setVariantQuantity(brick: any, variant: string, qty: number) {
		const quantities = setVariantQuantity(brick.presentation, variant, qty);
		brick.presentation.variant_quantities = quantities;
		brick.presentation.quantity = variantTotal(quantities);
	}

	/** Unidades añadidas antes de que el producto tuviera variantes cuentan como originales. */
	private seedVariantQuantities(brick: any) {
		const presentation = brick.presentation;
		const quantity = Number(presentation.quantity) || 0;
		if (!presentation.variant_quantities && quantity > 0) {
			presentation.variant_quantities = { [ORIGINAL_VARIANT_KEY]: quantity };
		}
	}

	/** "2 Original · 1 Whiskey" para el resumen del carrito; '' si no hay variantes elegidas. */
	variantBreakdown(item: any): string {
		return variantBreakdown(item && item.presentation);
	}

	/** Tocar la foto: un producto con variantes suma uno del original; el resto, como siempre. */
	tapMedia(brick: any) {
		if (this.isSoldOut(brick)) return;
		const inCart = !!(brick && brick.presentation && brick.presentation.clicked && brick.presentation.quantity > 0);
		this.cartTransactionFn(brick, inCart || this.hasVariants(brick) ? 'plusOne' : 'add');
	}

	async deleteBrick(brick) {
		const newBricks = this.cartUser.filter(element => element.$key !== brick.$key);
		this.cartUser = newBricks
		this.calculateTotal()
}

	/**
	 * Copia recortada del producto para el carrito (y de ahí a la línea de
	 * la orden): solo lo que la orden necesita, no el documento entero.
	 * `kitchen` tiene que viajar: es lo que decide si la orden nace
	 * pendiente para cocina (`isKitchenLine`); sin él la orden salía
	 * entregada y cocina nunca la veía.
	 */
	async updateCartUser(brick) {

		let brickInfo: any = {
			$key: brick.$key,
			name: brick.name,
			event_key: brick.event_key,
			category: brick.category,
			storeKey: brick.storeKey,
			storeCode: brick.storeCode,
			type: brick.type,
			kitchen: brick.kitchen === true,
			presentation: brick.presentation,
			image : {
				downloadURL : brick.image.downloadURL
			}
		}


		const searchBrick = this.cartUser.findIndex(element => element.$key === brick.$key);
		if (searchBrick !== -1) {
			brickInfo.presentation.subtotal = Number(brickInfo.presentation.quantity) * Number(brickInfo.presentation.price)
			this.cartUser[searchBrick] = brickInfo
			this.calculateTotal()
		} else {
			brickInfo.presentation.subtotal = Number(brickInfo.presentation.quantity) * Number(brickInfo.presentation.price)
			this.cartUser.push(brickInfo)
			this.calculateTotal()
		}

	}

	calculateTotal(){
		this.totalOrder = 0
		this.cartUser.forEach(element => {
			this.totalOrder += element.presentation.subtotal
		});
	}

	ionViewWillEnter() {
	}

	segmentChanged(e) {
		this.segmentS = e;
	}

	async onClick() {
		await new Promise(r => setTimeout(r, 5000));
		this.checkQuantityBricks()
	}
	
	/**
	 * Líneas de la orden a partir del carrito: la presentación con el
	 * producto dentro (`brick`). Un producto con variantes sale en una línea
	 * por opción elegida, cada una con su cantidad, su subtotal y `variant`
	 * (el original no la lleva); así "Squeeze" y "Squeeze · Whiskey" se
	 * cuentan aparte en el informe, el tiquete y la comanda.
	 */
	correctStructure(){
		let newStructure = []

		this.cartUser.forEach(element => {
			// Copia plana: Firestore rechaza undefined y objetos especiales, y
			// el producto del carrito es un objeto vivo del listener.
			const brick = JSON.parse(JSON.stringify(element))
			const presentation = JSON.parse(JSON.stringify(element.presentation))
			// El desglose por opción es estado de la caja: no viaja en la orden.
			delete presentation.variant_quantities
			if (brick.presentation) delete brick.presentation.variant_quantities

			const split = splitByVariant(element.presentation)
			if (split.length === 0) {
				presentation.brick = brick
				newStructure.push(presentation)
				return
			}
			const price = Number(presentation.price) || 0
			split.forEach(part => {
				const line: any = { ...presentation, quantity: part.quantity, subtotal: part.quantity * price, brick: JSON.parse(JSON.stringify(brick)) }
				if (part.variant) line.variant = part.variant
				newStructure.push(line)
			})
		});
		return newStructure
	}

	test(){

		console.error(this.cartUser);
		

		let a = this.correctStructure()
		console.warn(a);
	}

	async addOrder(loader, number) {

		// La orden se arma y se encola PRIMERO: si Firestore rechaza el documento
		// al validarlo, falla aquí sin haber tocado inventarios (antes se
		// descontaba primero y cada intento fallido restaba existencias sin
		// dejar orden). Los descuentos van después, también en cola.
		let bricks = this.correctStructure()
		// Con algún producto de cocina la orden nace PENDIENTE: la ve cocina,
		// que la pasa a preparación al imprimir la comanda y a entregada al
		// terminar. Sin cocina queda entregada de una vez, como siempre. La
		// marca plana `kitchen` permite consultarla sin leer líneas.
		const kitchen = bricks.some(line => isKitchenLine(line))
		const status = kitchen ? 'pending' : 'delivered'
		const courtesy = this.is_courtesy
		const company = this.cleanCompany(this.courtesyCompany)

		let data: any = {
			account: this.auth.token,
			added_date: new Date(),
			address: false, // mandatorio = false and pickup = true
			cashChange: false,
			combinationBrickEticket: false,
			countryCode: this.auth.countryCode || null,
			coupon: "",
			phone: '',
			storeKey: this.auth.storeKey || null,
			number: number,
			// isEticket: true,

			//cost_admin_details: adminCost,
			total_delivery: 0,
			tip: 0,
			// numeratedZone: order.hasOwnProperty('numeredSeats') ? true : false,
			// seatsReserved: order.hasOwnProperty('numeredSeats') ? order?.zoneSelected : null,
			bricks: bricks,
			status_time: { [status]: new Date() },
			status,
			payment: {
				type: courtesy ? COURTESY_PAYMENT : this.segmentS
			},
			pickup: true,
			systemOs: 'web',
			scheduled : false,
			event_order : true,
			total: this.totalOrder,
			change: this.totalOrder,
			event_key : this.eventKey,
			event_category_key : this.categoryEventKey,
			kitchen,
		}
		if (kitchen) data.kitchen_status = 'pending'
		// Regalía: sin cobro y con la empresa a la que va (ver shared/courtesy).
		if (courtesy) {
			data.courtesy = true
			data.courtesy_company = company
			this.rememberCourtesyCompany(company)
		}
		// No se espera la confirmación del servidor: sin conexión la orden
		// queda guardada en el dispositivo y se envía sola al volver la señal.
		// Siempre sobre el mismo id de esta orden: nunca dos documentos.
		this.api.setDocumentQueued("orders", this.draftOrderId, data);

		// Cada producto se descuenta por su cuenta y en paralelo (no se suman
		// esperas de red). Si uno falla no se detiene la venta ya registrada:
		// se deja constancia para corregir ese inventario a mano.
		await Promise.all(this.cartUser.map(element =>
			this.updateInventory(element.presentation, element.$key).catch(error => {
				console.error(`No se pudo descontar el inventario de ${element.name}:`, error);
				this.mensajeAlerta('warning', `La orden #${number} se guardó, pero no se pudo descontar el inventario de ${element.name}`);
			})
		))
		loader.dismiss()

		const offline = this.connectivity.isOffline;
		Swal.fire({
            position: 'top-end',
            icon: offline ? "info" : "success",
            title: offline
                ? `Orden #${data.number} guardada en este dispositivo · se enviará al volver la conexión`
                : (courtesy ? `Regalía #${data.number} registrada para ${company}${kitchen ? ' · enviada a cocina' : ''}`
                    : (kitchen ? `Orden #${data.number} enviada a cocina` : `Tú orden ha sido creada, # ${data.number}`)),
            showConfirmButton: false,
            timer: offline ? 5000 : 3500,
            toast: true
        });

		this.modalController.dismiss(data);
	}

	/**
	 * Candado del botón "completar orden": un doble toque en pantalla táctil
	 * llegaba a lanzar dos veces el flujo antes de que apareciera el loader,
	 * y salían dos órdenes con números distintos (y el inventario descontado
	 * dos veces). Mientras una está en curso, los toques siguientes se ignoran.
	 */
	submitting: boolean = false
	/**
	 * Id del documento de la orden en curso, fijo desde el primer intento:
	 * si por cualquier motivo el flujo corriera dos veces, la segunda
	 * escritura cae sobre el mismo documento en vez de crear otro.
	 */
	private draftOrderId: string = ''

	async generateOrder() {
		if (this.submitting) return
		if (!this.eventKey || !this.categoryEventKey) {
			this.alertController.create({
				cssClass: 'alertStyle',
				header: 'Falta la zona',
				message: 'Elige de qué zona sale la orden antes de completarla.',
				buttons: ['OK']
			}).then(alert => alert.present())
			return
		}
		this.submitting = true
		if (!this.draftOrderId) this.draftOrderId = this.api.db.createId()
		try {
			const loader = await this.loadingCtrl.create({
				backdropDismiss: false,
			})
			await loader.present()
			await this.createNumberOrder(loader)
		} finally {
			this.submitting = false
		}
	}

	private getRndInteger = () => {
		let min = 1;
		let max = 99_999_999;
		return Math.floor(Math.random() * (max - min)) + min;
	}

	/**
	 * Tiempo máximo de cada lectura previa a la orden. Con la red a medias
	 * (wifi sin salida, 3G que se cae) Firestore puede tardar 10-20 s en
	 * darse por vencido y responder desde caché; la caja no puede esperar
	 * eso con un cliente enfrente. Pasado el límite se usa lo que ya trae el
	 * carrito, que viene del mismo listener en vivo.
	 */
	private static readonly READ_TIMEOUT_MS = 4000;
	/** En cuanto una lectura se agota, las siguientes de la misma orden esperan solo esto. */
	private static readonly READ_TIMEOUT_DEGRADED_MS = 600;
	private readsTimingOut = false;

	/** Espera la promesa hasta `ms`; si no responde o falla, devuelve el fallback en vez de rechazar. */
	private withTimeout<T>(promise: Promise<T>, ms: number, fallback: (reason: any) => T): Promise<T> {
		return new Promise<T>(resolve => {
			let settled = false;
			const limit = this.readsTimingOut ? Math.min(ms, CreateOrderEventComponent.READ_TIMEOUT_DEGRADED_MS) : ms;
			const timer = setTimeout(() => {
				if (settled) return;
				settled = true;
				this.readsTimingOut = true;
				resolve(fallback('timeout'));
			}, limit);
			promise.then(value => {
				if (settled) return;
				settled = true;
				clearTimeout(timer);
				resolve(value);
			}, reason => {
				if (settled) return;
				settled = true;
				clearTimeout(timer);
				resolve(fallback(reason));
			});
		});
	}

	private isPermissionDenied(error: any): boolean {
		return !!error && (error.code === 'permission-denied' || /insufficient permissions/i.test(String(error.message || '')));
	}

	async createNumberOrder(loader) {
		this.readsTimingOut = false;
		try {
			const number = await this.uniqueOrderNumber();
			await this.getInventory(loader, number);
		} catch (error) {
			this.failOrder(loader, error);
		}
	}

	/**
	 * Número de orden aleatorio de 8 dígitos. Se comprueba contra `orders`
	 * que no exista, pero si la consulta no responde a tiempo o falla por la
	 * red se usa igual: la colisión es despreciable y no vale detener la
	 * venta. Solo un permiso denegado sí detiene, porque entonces tampoco se
	 * podría guardar la orden y hay que arreglar las reglas.
	 */
	private async uniqueOrderNumber(): Promise<number> {
		for (let attempt = 0; attempt < 5; attempt++) {
			const number = this.getRndInteger();
			let denied: any = null;
			const taken = await this.withTimeout(
				this.api.getRef('orders').ref.where('number', '==', number).get(this.readOptions()).then(snapshots => snapshots.size > 0),
				CreateOrderEventComponent.READ_TIMEOUT_MS,
				reason => {
					if (this.isPermissionDenied(reason)) denied = reason;
					else console.warn('No se pudo comprobar el número de orden; se usa igual:', reason);
					return false;
				},
			);
			if (denied) throw denied;
			if (!taken) return number;
		}
		return this.getRndInteger();
	}

	/**
	 * Cualquier error del flujo cierra el loader: la pantalla nunca se queda
	 * colgada. El detalle técnico va en el aviso para poder diagnosticarlo
	 * desde la caja sin abrir la consola.
	 */
	private async failOrder(loader, error) {
		console.error('No se pudo crear la orden:', error);
		const loading = await this.loadingCtrl.getTop()
		if (loading) {
			await loading.dismiss()
		}
		loader.dismiss();
		const detail = error && (error.message || error.code) ? String(error.message || error.code) : '';
		const code = error && error.code ? String(error.code) : '';
		const hint = this.isPermissionDenied(error)
			? 'Este usuario no tiene permiso en Firestore para leer o guardar órdenes: hay que revisar las reglas.'
			: 'El carrito sigue igual: revisa e intenta de nuevo.';
		this.alertController.create({
			cssClass: 'alertStyle',
			header: 'No se pudo crear la orden',
			message: hint
				+ (detail ? `<br><small style="color:#6b7280">Detalle${code ? ` (${code})` : ''}: ${detail.replace(/</g, '&lt;').slice(0, 220)}</small>` : ''),
			buttons: ['OK']
		}).then(alert => alert.present())
	}

	async getInventory(loader, number) {
		let have_inventory = true;
		let noInventoryName = [];

		// En paralelo y con tiempo límite: si la presentación no está en caché
		// o la red no responde, vale el inventario que ya trae el carrito
		// (viene del mismo listener en vivo).
		const inventories = await Promise.all(this.cartUser.map(element => this.withTimeout(
			this.api.getDocument(`bricks/${element.$key}/presentations/`, element.presentation.$key, this.readOptions()),
			CreateOrderEventComponent.READ_TIMEOUT_MS,
			() => ({ inventory: element.presentation.inventory }),
		)));
		this.cartUser.forEach((element, i) => {
			const data: any = inventories[i] || { inventory: element.presentation.inventory };
			if (element.presentation.quantity > data['inventory']) {
				have_inventory = false;
				element.missing_inventory = true;
				element.inventory = data['inventory'];
				noInventoryName.push("<h5>" + element.name + "</h5>");
			}
		});

		if (have_inventory) {
			console.warn("TODO BIEN HACE COMPRA");
			await this.addOrder(loader, number)
		} else {
			this.inventoryMessage(loader, number, noInventoryName);
		}
	}

	async inventoryMessage(loader, number, product) {

		const loading = await this.loadingCtrl.getTop()
		if (loading) {
			await loading.dismiss()
		}
		loader.dismiss();
		let newProd = []
		for (let i = 0; i < product.length; i++) {
			const element = product[i];
			let styleProd = `${element}`
			newProd.push(styleProd)
		}
		let products = `${newProd.join('')}`
		this.alertController.create({
			cssClass: 'alertStyle',
			header: 'Inventario insuficiente',
			message: `${products}`,
			buttons: ['OK']
		}).then(alert => {
			alert.present();
		})
	}

	/**
	 * Descuenta lo vendido de la presentación. `brickKey` es el respaldo por
	 * si la presentación llegó al carrito sin `brick_key`; y si le falta su
	 * propio `$key` se ubica entre las presentaciones del producto por nombre
	 * o se toma la principal. Así nunca se llama a Firestore con un id vacío.
	 */
	async updateInventory(element, brickKey?: string) {
		const brick_key = element.brick_key || brickKey;
		if (!brick_key) {
			throw new Error('La presentación no trae la llave del producto.');
		}

        let AllPresentations = await this.withTimeout(
            this.api.getAllDocuments(`bricks/${brick_key}/presentations`, this.readOptions()).then(presentations => presentations || []),
            CreateOrderEventComponent.READ_TIMEOUT_MS,
            () => [],
        );

        if (!element.$key) {
            const match = AllPresentations.find(p => p.presentation === element.presentation)
                || AllPresentations.find(p => p.main === true)
                || AllPresentations[0];
            if (!match) throw new Error('La presentación no trae su llave y no se pudo ubicar en el producto.');
            element.$key = match.$key;
        }
        element.brick_key = brick_key;

        let findPresentation = AllPresentations.find(presentation => presentation.$key === element.$key);

        // Sin caché de la presentación (sin conexión y sin haberla escuchado)
        // se trabaja con la copia que trae el carrito. En ese caso no se sabe
        // cómo están las demás presentaciones del brick, así que no se toca
        // la visibilidad del brick completo.
        const knowsAllPresentations = !!findPresentation;
        if (!findPresentation) {
            findPresentation = element;
            AllPresentations = [element];
        }

        if (findPresentation) {
            const stored = findPresentation['inventory'];
            const storedNumber = Number(stored);
            const quantity = Number(element.quantity) || 0;
            let inventarioActual = (isFinite(storedNumber) ? storedNumber : 0) - quantity
            let flagVisible = true;
            let flagMain = false;
            let update_data = {};

            // Booleano siempre: un undefined aquí hace que Firestore rechace la escritura.
            if (findPresentation.hasOwnProperty('main')) flagMain = element.main === true;

            if (typeof stored === 'number' && isFinite(stored)) {
                // Resta atómica en el servidor: si dos cajas venden el mismo
                // producto a la vez, o una vende sin conexión y sincroniza
                // después, cada descuento se aplica sobre el valor real y
                // ninguno pisa al otro. En la copia local se ve descontado ya.
                update_data["inventory"] = firebase.firestore.FieldValue.increment(-quantity)
            } else {
                // Varias pantallas guardan el inventario como texto ("100").
                // Sobre un texto, increment() no resta: lo reemplaza por -N y
                // el producto queda "sin inventario". Se escribe el número.
                update_data["inventory"] = Math.max(inventarioActual, 0)
            }
            update_data["main"] = flagMain

            if (inventarioActual <= 10) update_data["inventory_visible"] = true;

            if (inventarioActual <= 0) {
                flagVisible = false;
                update_data["inventory_visible"] = false;
            }

            if (!flagVisible) {
                let props = [];
                Object.entries(findPresentation).forEach(([key, partner]) => {
                    if (key.includes("visible") && key != "inventory_visible") props.push(key);
                });

                if (props.length > 0) {
                    for (let j = 0; j < props.length; j++) {
                        const element = props[j];
                        update_data[`${element}`] = false;
                    }
                }
            }

            Object.assign(findPresentation, update_data, { inventory: inventarioActual }); // modificacion de objeto que se esta evaluando para obtener nuevos valores

            // En cola: no se espera al servidor (ver ApiService.updateDocumentQueued).
            this.api.updateDocumentQueued(`bricks/${brick_key}/presentations`, element.$key, update_data);

            // verificando si hay alguna presentacion disponible
            let presentation_visible = AllPresentations.filter(presentation => presentation.visible != false);

            if (knowsAllPresentations && presentation_visible.length == 0) {
                this.api.updateDocumentQueued('bricks', brick_key, {
                    visible: false,
                });
            }
        }
    }

	mensajeAlerta(icono: SweetAlertIcon, mensaje: string) {
		Swal.fire({
			position: 'top-end',
			icon: icono,
			title: mensaje,
			showConfirmButton: false,
			timer: 4500,
			toast: true,
		});
	}

	isButtonEnabled(): boolean {
		if (this.cartUser.length === 0 || !this.categoryEventKey) return false;
		// Regalía: no hay método de pago que elegir, pero sí a quién va.
		if (this.is_courtesy) return this.cleanCompany(this.courtesyCompany).length >= 2;
		return this.segmentS != "";
	}

	// ═══════════════════════════════════════════════════════════════════════
	//  REGALÍAS
	// ═══════════════════════════════════════════════════════════════════════

	/** El carrito tiene productos y suma $0: la orden es una regalía. */
	get is_courtesy(): boolean {
		return this.cartUser.length > 0 && Number(this.totalOrder) === 0;
	}

	selectCourtesyCompany(name: string) {
		this.courtesyCompany = name;
	}

	isCompanySelected(name: string): boolean {
		return companyKey(name) === companyKey(this.courtesyCompany);
	}

	private cleanCompany(name: string): string {
		return String(name || '').replace(/\s+/g, ' ').trim();
	}

	private courtesyStorageKey(): string {
		return `${CreateOrderEventComponent.COURTESY_KEY}:${this.eventKey || ''}`;
	}

	/** Empresas usadas en este dispositivo para este evento, la más reciente primero. */
	private readCourtesyCompanies(): string[] {
		try {
			const raw = localStorage.getItem(this.courtesyStorageKey());
			const list = raw ? JSON.parse(raw) : [];
			return Array.isArray(list) ? list.map(x => String(x)) : [];
		} catch (e) {
			return [];
		}
	}

	private rememberCourtesyCompany(name: string) {
		const clean = this.cleanCompany(name);
		if (!clean) return;
		const list = [clean].concat(this.readCourtesyCompanies().filter(c => companyKey(c) !== companyKey(clean)))
			.slice(0, CreateOrderEventComponent.COURTESY_MAX);
		try {
			localStorage.setItem(this.courtesyStorageKey(), JSON.stringify(list));
		} catch (e) {
			// Sin almacenamiento local (modo privado, cuota): la regalía se guarda igual.
		}
		this.courtesyCompanies = knownCompanies([], list.concat(this.courtesyCompanies));
	}
}
